/**
 * Dilithium RKS Wrapper
 * 
 * Safe wrapper around the experimental Dilithium RKS implementation
 * with network enforcement, telemetry, and error handling.
 */

import { isFeatureAvailable, getFeatureNetworkRestriction, FEATURES } from '@/types/settings'
import { useUserStore } from '@/lib/store/user'
import { useExperimentalStore } from '@/lib/store/experimental'

// Type definitions for the RKS library
interface DilithiumRKSLib {
  deriveChildPublicKey(
    parentPublicKey: Uint8Array,
    chainCode: Uint8Array,
    index: number
  ): Promise<{
    publicKey: Uint8Array
    chainCode: Uint8Array
  }>
  
  verifyRerandomizedKey(publicKey: Uint8Array): Promise<boolean>
}

declare global {
  interface Window {
    DilithiumRKS: new () => DilithiumRKSLib
  }
}

export class DilithiumRKSWrapper {
  private rks: DilithiumRKSLib | null = null
  private initialized = false
  private initPromise: Promise<void> | null = null
  
  /**
   * Initialize the RKS library (lazy loading)
   */
  private async init(): Promise<void> {
    // Return existing promise if initialization is in progress
    if (this.initPromise) {
      return this.initPromise
    }
    
    // Already initialized
    if (this.initialized && this.rks) {
      return
    }
    
    this.initPromise = this.performInit()
    return this.initPromise
  }
  
  private async performInit(): Promise<void> {
    const userSettings = useUserStore.getState().settings
    const experimentalStore = useExperimentalStore.getState()
    
    // Check if feature is enabled
    if (!isFeatureAvailable('DILITHIUM_RKS', userSettings)) {
      throw new Error('Dilithium RKS feature is not enabled. Enable alpha features in settings.')
    }
    
    // Check if user has accepted the risks
    if (!experimentalStore.checkFeatureAcceptance('DILITHIUM_RKS')) {
      // Trigger the consent modal
      experimentalStore.requestFeatureAccess('DILITHIUM_RKS')
      throw new Error('User must accept experimental feature risks before using Dilithium RKS')
    }
    
    // Check network restrictions
    const networkRestriction = getFeatureNetworkRestriction('DILITHIUM_RKS', userSettings)
    const network = this.getCurrentNetwork()
    
    if (networkRestriction === 'testnet-only' && network === 'mainnet') {
      throw new Error('Dilithium RKS is restricted to testnet only')
    }
    
    // Load the RKS library
    if (!window.DilithiumRKS) {
      // Try to load the script dynamically
      await this.loadRKSScript()
    }
    
    if (!window.DilithiumRKS) {
      throw new Error('Failed to load Dilithium RKS library')
    }
    
    // Initialize RKS instance
    this.rks = new window.DilithiumRKS()
    this.initialized = true
    
    // Log initialization for audit trail
    console.warn('🚨 EXPERIMENTAL: Dilithium RKS initialized', {
      timestamp: new Date().toISOString(),
      network,
      feature: 'DILITHIUM_RKS'
    })
    
    // Track initialization
    this.trackUsage('initialized', { network })
  }
  
  /**
   * Load RKS script dynamically
   */
  private async loadRKSScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = '/js/core/dilithium-rks.js'
      script.async = true
      
      script.onload = () => {
        console.log('✅ Dilithium RKS script loaded')
        resolve()
      }
      
      script.onerror = () => {
        reject(new Error('Failed to load Dilithium RKS script'))
      }
      
      document.head.appendChild(script)
    })
  }
  
  /**
   * Get current network from store or URL
   */
  private getCurrentNetwork(): 'mainnet' | 'testnet' {
    // Check URL params
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('network') === 'testnet') {
      return 'testnet'
    }
    
    // TODO: Get from user store
    // For now, default to testnet for experimental features
    return 'testnet'
  }
  
  /**
   * Track usage for telemetry
   */
  private trackUsage(action: string, data?: any): void {
    // Log usage for audit trail
    console.log('📊 RKS Usage:', {
      feature: 'DILITHIUM_RKS',
      action,
      data,
      timestamp: new Date().toISOString(),
      maturity: FEATURES.DILITHIUM_RKS.maturity
    })
  }
  
  /**
   * Derive non-hardened child public key
   * This is the main function for watch-only wallet support
   */
  async deriveChildPublicKey(
    parentPublicKey: Uint8Array,
    chainCode: Uint8Array,
    index: number
  ): Promise<{
    publicKey: Uint8Array
    chainCode: Uint8Array
  }> {
    // Ensure initialized
    await this.init()
    
    if (!this.rks) {
      throw new Error('RKS not initialized')
    }
    
    // Validate inputs
    if (index >= 0x80000000) {
      throw new Error('Index must be non-hardened (< 2^31)')
    }
    
    if (parentPublicKey.length !== 1472) { // Dilithium3 public key size
      throw new Error('Invalid public key size for Dilithium3')
    }
    
    if (chainCode.length !== 32) {
      throw new Error('Chain code must be 32 bytes')
    }
    
    // Log derivation attempt
    console.warn('⚠️ EXPERIMENTAL: Deriving non-hardened Dilithium key', {
      index,
      network: this.getCurrentNetwork()
    })
    
    try {
      // Perform derivation
      const result = await this.rks.deriveChildPublicKey(parentPublicKey, chainCode, index)
      
      // Verify the derived key
      const isValid = await this.rks.verifyRerandomizedKey(result.publicKey)
      if (!isValid) {
        throw new Error('Derived key failed validation')
      }
      
      // Track successful derivation
      this.trackUsage('derive_success', { index })
      
      return result
    } catch (error) {
      // Track failure
      this.trackUsage('derive_failure', { 
        index, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      })
      
      throw error
    }
  }
  
  /**
   * Verify a rerandomized public key
   */
  async verifyPublicKey(publicKey: Uint8Array): Promise<boolean> {
    await this.init()
    
    if (!this.rks) {
      throw new Error('RKS not initialized')
    }
    
    try {
      const isValid = await this.rks.verifyRerandomizedKey(publicKey)
      this.trackUsage('verify', { valid: isValid })
      return isValid
    } catch (error) {
      this.trackUsage('verify_error', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      })
      return false
    }
  }
  
  /**
   * Check if RKS is available and enabled
   */
  static isAvailable(): boolean {
    const userSettings = useUserStore.getState().settings
    return isFeatureAvailable('DILITHIUM_RKS', userSettings)
  }
  
  /**
   * Get feature status
   */
  static getStatus(): {
    enabled: boolean
    network: 'mainnet' | 'testnet'
    maturity: 'alpha' | 'beta' | 'stable'
    networkRestriction: string
  } {
    const userSettings = useUserStore.getState().settings
    const enabled = isFeatureAvailable('DILITHIUM_RKS', userSettings)
    const networkRestriction = getFeatureNetworkRestriction('DILITHIUM_RKS', userSettings)
    
    return {
      enabled,
      network: networkRestriction === 'testnet-only' ? 'testnet' : 'mainnet',
      maturity: FEATURES.DILITHIUM_RKS.maturity,
      networkRestriction
    }
  }
}

// Singleton instance
let rksInstance: DilithiumRKSWrapper | null = null

/**
 * Get RKS wrapper instance
 */
export function getDilithiumRKS(): DilithiumRKSWrapper {
  if (!rksInstance) {
    rksInstance = new DilithiumRKSWrapper()
  }
  return rksInstance
}